import { safeFetch } from './api';
import type { OrgSpecialRequestTemplate } from '../types';

/**
 * Organization Special Request Templates — client access for the
 * DEDICATED org template store (/api/admin/org-special-request-templates).
 * Fully separate from the applicant special-request templates
 * (src/lib/specialRequestTemplates.ts); the two never mix.
 */

export type OrgTemplateOrgType = 'referrer' | 'employer' | 'sponsor';

export async function fetchOrgSpecialRequestTemplates(opts?: {
  orgType?: OrgTemplateOrgType;
  activeOnly?: boolean;
}): Promise<OrgSpecialRequestTemplate[]> {
  const params = new URLSearchParams();
  if (opts?.orgType) params.set('orgType', opts.orgType);
  if (opts?.activeOnly) params.set('activeOnly', 'true');
  const qs = params.toString();
  const data = await safeFetch(`/api/admin/org-special-request-templates${qs ? `?${qs}` : ''}`);
  return (data.templates || []) as OrgSpecialRequestTemplate[];
}

export async function createOrgSpecialRequestTemplate(
  fields: Partial<OrgSpecialRequestTemplate>,
): Promise<OrgSpecialRequestTemplate> {
  const data = await safeFetch('/api/admin/org-special-request-templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  return data.template as OrgSpecialRequestTemplate;
}

export async function updateOrgSpecialRequestTemplate(
  templateId: string,
  fields: Partial<OrgSpecialRequestTemplate>,
): Promise<OrgSpecialRequestTemplate> {
  const data = await safeFetch(`/api/admin/org-special-request-templates/${templateId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  return data.template as OrgSpecialRequestTemplate;
}

export async function deleteOrgSpecialRequestTemplate(templateId: string): Promise<void> {
  await safeFetch(`/api/admin/org-special-request-templates/${templateId}`, { method: 'DELETE' });
}

export interface OrgOption {
  orgId: string;
  orgName: string;
  hasRecipient: boolean;
}

export async function fetchOrgOptions(orgType: OrgTemplateOrgType): Promise<OrgOption[]> {
  const data = await safeFetch(`/api/admin/org-special-requests/org-options?orgType=${orgType}`);
  return (data.organizations || []) as OrgOption[];
}

export interface OrgBulkSendResult {
  orgId: string;
  ok: boolean;
  requestId?: string;
  status?: number;
  error?: string;
}

export async function sendOrgSpecialRequestsBulk(args: {
  orgType: OrgTemplateOrgType;
  orgIds: string[];
  templateId?: string;
  fields?: Record<string, any>;
  dueDate?: string | null;
  noDeadline?: boolean;
}): Promise<{ results: OrgBulkSendResult[]; sent: number; failed: number }> {
  const data = await safeFetch('/api/admin/org-special-requests/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  return { results: data.results || [], sent: data.sent || 0, failed: data.failed || 0 };
}
